import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Play, Loader2, Lock, Unlock, ChevronDown, ChevronUp,
  CheckCircle2, AlertCircle, Upload, X, Copy, Check, Maximize2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ─── Image Lightbox (portal) ───────────────────────────────────────────────────
function ImageLightbox({ src, onClose }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
        data-testid="lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Image */}
      <div
        className="relative max-w-[92vw] max-h-[88vh] flex items-center justify-center"
        onClick={e => e.stopPropagation()}
      >
        <img
          src={src}
          alt="Result"
          className="max-w-full max-h-[88vh] object-contain rounded-xl shadow-2xl"
          data-testid="lightbox-image"
        />
      </div>

      {/* Bottom bar */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-3">
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-full backdrop-blur-sm transition-colors border border-white/10"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          Open original
        </a>
        <span className="hidden sm:inline text-white/30 text-xs">ESC to close</span>
      </div>
    </div>,
    document.body
  );
}


function useLocalStorage(key, init) {
  const [val, setVal] = useState(() => {
    try { return localStorage.getItem(key) || init; } catch { return init; }
  });
  const set = (v) => {
    setVal(v);
    try { localStorage.setItem(key, v); } catch {}
  };
  return [val, set];
}

// ─── URL builder ───────────────────────────────────────────────────────────────
function interpolatePath(path, fields) {
  return path.replace(/\{(\w+)\}/g, (_, name) => fields[name] || `{${name}}`);
}

// ─── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const color =
    status >= 200 && status < 300 ? 'text-emerald-400 bg-emerald-400/10' :
    status >= 400 && status < 500 ? 'text-amber-400 bg-amber-400/10' :
    status >= 500 ? 'text-red-400 bg-red-400/10' :
    'text-slate-400 bg-slate-700';
  const icon =
    status >= 200 && status < 300
      ? <CheckCircle2 className="w-3.5 h-3.5" />
      : <AlertCircle className="w-3.5 h-3.5" />;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-bold ${color}`}>
      {icon} {status || 'Error'}
    </span>
  );
}

// ─── Individual field renderer ─────────────────────────────────────────────────
function FieldInput({ param, value, onChange }) {
  const IN_PILL = {
    body: 'bg-slate-700 text-slate-400',
    query: 'bg-sky-900/50 text-sky-400',
    path: 'bg-purple-900/50 text-purple-400',
    header: 'bg-amber-900/50 text-amber-400',
  };

  const meta = (
    <div className="flex items-center gap-1.5 mb-1.5">
      <code className="text-xs font-mono font-semibold text-slate-200">{param.name}</code>
      {param.required && (
        <span className="text-[9px] font-bold text-red-400 uppercase tracking-widest">required</span>
      )}
      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${IN_PILL[param.in] || IN_PILL.body}`}>
        {param.in}
      </span>
      <span className="text-[10px] text-slate-600 font-mono">{param.type}</span>
    </div>
  );

  const base = "w-full bg-slate-800 border border-slate-700 text-slate-200 text-xs font-mono rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500/80 placeholder-slate-600 transition-colors";

  if (param.enum) {
    return (
      <div>
        {meta}
        <select value={value} onChange={e => onChange(e.target.value)}
          className={`${base} cursor-pointer`}>
          <option value="">-- select --</option>
          {param.enum.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>
    );
  }

  if (param.type === 'boolean') {
    return (
      <div>
        {meta}
        <select value={value} onChange={e => onChange(e.target.value)} className={`${base} cursor-pointer`}>
          <option value="">-- select --</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      </div>
    );
  }

  if (param.type === 'object') {
    return (
      <div>
        {meta}
        <textarea
          value={value} onChange={e => onChange(e.target.value)}
          placeholder={param.name === 'size_chart_cm'
            ? `{"S": {"chest": 84, "waist": 68}, "M": {"chest": 88, "waist": 72}}`
            : '{ }'}
          rows={3}
          className={`${base} resize-y`}
        />
      </div>
    );
  }

  return (
    <div>
      {meta}
      <input
        type={param.type === 'integer' || param.type === 'float' ? 'number' : 'text'}
        step={param.type === 'float' ? 'any' : undefined}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={param.placeholder || param.description?.split('.')[0] || param.name}
        className={base}
      />
    </div>
  );
}

// ─── File field ────────────────────────────────────────────────────────────────
function FileFieldInput({ param, fileName, onChange }) {
  const ref = useRef();
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <code className="text-xs font-mono font-semibold text-slate-200">{param.name}</code>
        {param.required && (
          <span className="text-[9px] font-bold text-red-400 uppercase tracking-widest">required</span>
        )}
        <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-slate-700 text-slate-400">body</span>
        <span className="text-[10px] text-slate-600 font-mono">file</span>
      </div>
      <div
        className="flex items-center gap-2 bg-slate-800 border border-dashed border-slate-600 rounded-lg px-3 py-2.5 cursor-pointer hover:border-slate-500 transition-colors"
        onClick={() => ref.current?.click()}
      >
        {fileName ? (
          <>
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
            <span className="text-xs text-slate-300 font-mono truncate flex-1">{fileName}</span>
            <button
              onClick={e => { e.stopPropagation(); onChange(null, null); ref.current.value = ''; }}
              className="text-slate-500 hover:text-red-400 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            <Upload className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            <span className="text-xs text-slate-500">Click to upload image (JPEG / PNG / WEBP)</span>
          </>
        )}
        <input ref={ref} type="file" accept="image/*" className="hidden"
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) onChange(f, f.name);
          }} />
      </div>
    </div>
  );
}

// ─── Credentials bar ───────────────────────────────────────────────────────────
function CredentialsBar({ apiKey, setApiKey, partnerId, setPartnerId }) {
  const [open, setOpen] = useState(!apiKey);
  const isSet = !!apiKey;

  return (
    <div className="rounded-xl overflow-hidden border border-slate-800">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-900 hover:bg-slate-800/80 transition-colors"
        data-testid="credentials-toggle"
      >
        <div className="flex items-center gap-2">
          {isSet
            ? <Lock className="w-3.5 h-3.5 text-emerald-400" />
            : <Unlock className="w-3.5 h-3.5 text-amber-400" />}
          <span className={`text-xs font-semibold ${isSet ? 'text-emerald-400' : 'text-amber-400'}`}>
            {isSet ? 'Credentials saved' : 'Enter your credentials to test'}
          </span>
        </div>
        {open
          ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" />
          : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
      </button>

      {open && (
        <div className="px-4 py-3 bg-slate-900/50 space-y-2.5 border-t border-slate-800">
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1.5">
              X-API-Key
            </label>
            <input
              type="text" value={apiKey} onChange={e => setApiKey(e.target.value)}
              placeholder="nk_your_api_key_here"
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-xs font-mono rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500/80 placeholder-slate-600 transition-colors"
              data-testid="api-key-input"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1.5">
              X-Partner-Id
            </label>
            <input
              type="text" value={partnerId} onChange={e => setPartnerId(e.target.value)}
              placeholder="NK-XXXXXX or UUID"
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-xs font-mono rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500/80 placeholder-slate-600 transition-colors"
              data-testid="partner-id-input"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main ApiTester ─────────────────────────────────────────────────────────────
export function ApiTester({ doc }) {
  const [apiKey, setApiKey] = useLocalStorage('nk_tester_api_key', '');
  const [partnerId, setPartnerId] = useLocalStorage('nk_tester_partner_id', '');
  const [fieldValues, setFieldValues] = useState({});
  const [fileValues, setFileValues] = useState({});
  const [fileNames, setFileNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const testPath = doc?.testPath || doc?.path;

  // Reset state when endpoint changes
  useEffect(() => {
    setFieldValues({});
    setFileValues({});
    setFileNames({});
    setResult(null);
  }, [testPath]);

  if (!doc?.method || doc?.noTester) {
    if (doc?.noTester) {
      return (
        <div className="bg-slate-950 rounded-xl border border-slate-800 p-5 text-center">
          <AlertCircle className="w-8 h-8 text-slate-600 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-400 mb-1">Interactive testing not available</p>
          <p className="text-xs text-slate-600 leading-relaxed">{doc.noTesterReason || 'This endpoint requires partner dashboard authentication. Use the cURL example above.'}</p>
        </div>
      );
    }
    return null;
  }

  const params = (doc.params || []).filter(p => p.in !== 'info' && p.name !== 'X-API-Key' && p.name !== 'X-Partner-Id');
  const pathParams = params.filter(p => p.in === 'path');
  const queryParams = params.filter(p => p.in === 'query');
  const bodyParams = params.filter(p => p.in === 'body');
  const extraHeaderParams = params.filter(p => p.in === 'header');
  const isMultipart = !!doc.requiresMultipart;

  const handleField = (name, val) => setFieldValues(prev => ({ ...prev, [name]: val }));
  const handleFile = (name, file, name_) => {
    setFileValues(prev => ({ ...prev, [name]: file }));
    setFileNames(prev => ({ ...prev, [name]: name_ || null }));
    if (!file) handleField(name, '');
  };

  // Build live URL display
  const liveUrl = (() => {
    const filled = {};
    pathParams.forEach(p => { filled[p.name] = fieldValues[p.name] || `{${p.name}}`; });
    let url = interpolatePath(testPath, filled);
    const qp = {};
    queryParams.forEach(p => { if (fieldValues[p.name]) qp[p.name] = fieldValues[p.name]; });
    const qs = new URLSearchParams(qp).toString();
    if (qs) url += `?${qs}`;
    return url;
  })();

  const handleSend = async () => {
    setLoading(true);
    setResult(null);
    const start = Date.now();

    try {
      const headers = {};
      if (apiKey) headers['X-API-Key'] = apiKey;
      if (partnerId) headers['X-Partner-Id'] = partnerId;
      extraHeaderParams.forEach(p => {
        if (fieldValues[p.name]) headers[p.name] = fieldValues[p.name];
      });

      const pathFilled = {};
      pathParams.forEach(p => { pathFilled[p.name] = fieldValues[p.name] || ''; });
      let urlPath = interpolatePath(testPath, pathFilled);
      const qp = {};
      queryParams.forEach(p => { if (fieldValues[p.name]) qp[p.name] = fieldValues[p.name]; });
      const qs = new URLSearchParams(qp).toString();
      if (qs) urlPath += `?${qs}`;

      let fetchBody;
      if (doc.method !== 'GET' && doc.method !== 'DELETE') {
        if (isMultipart) {
          const fd = new FormData();
          bodyParams.forEach(p => {
            if (p.isFile) {
              const f = fileValues[p.name];
              if (f) fd.append(p.name, f);
            } else if (fieldValues[p.name] !== undefined && fieldValues[p.name] !== '') {
              fd.append(p.name, fieldValues[p.name]);
            }
          });
          fetchBody = fd;
        } else {
          const bodyObj = {};
          bodyParams.forEach(p => {
            const v = fieldValues[p.name];
            if (v === undefined || v === '') return;
            if (p.type === 'integer') bodyObj[p.name] = parseInt(v, 10);
            else if (p.type === 'float') bodyObj[p.name] = parseFloat(v);
            else if (p.type === 'boolean') bodyObj[p.name] = v === 'true';
            else if (p.type === 'object') { try { bodyObj[p.name] = JSON.parse(v); } catch { bodyObj[p.name] = v; } }
            else bodyObj[p.name] = v;
          });
          if (Object.keys(bodyObj).length) {
            headers['Content-Type'] = 'application/json';
            fetchBody = JSON.stringify(bodyObj);
          }
        }
      }

      const res = await fetch(`${BACKEND_URL}${urlPath}`, {
        method: doc.method,
        headers,
        body: fetchBody,
      });

      const elapsed = Date.now() - start;
      let body;
      let imageUrl = null;
      try {
        const parsed = await res.json();
        body = JSON.stringify(parsed, null, 2);
        imageUrl = parsed?.image_url || null;
      }
      catch { body = await res.text(); }

      setResult({ status: res.status, body, time: elapsed, ok: res.ok, imageUrl });
    } catch (err) {
      setResult({ status: 0, body: `Network error: ${err.message}`, time: Date.now() - start, ok: false });
    } finally {
      setLoading(false);
    }
  };

  const copyResponse = () => {
    if (!result?.body) return;
    navigator.clipboard.writeText(result.body);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const METHOD_COLOR = {
    GET: 'bg-sky-900/60 text-sky-300',
    POST: 'bg-emerald-900/60 text-emerald-300',
    PUT: 'bg-amber-900/60 text-amber-300',
    DELETE: 'bg-red-900/60 text-red-300',
  };

  return (
    <div className="bg-slate-950 rounded-xl border border-slate-800 overflow-hidden flex flex-col">
      {/* Credentials */}
      <div className="px-4 pt-4 pb-0">
        <CredentialsBar apiKey={apiKey} setApiKey={setApiKey} partnerId={partnerId} setPartnerId={setPartnerId} />
      </div>

      {/* URL Bar */}
      <div className="px-4 py-3 mt-3 mx-4 bg-slate-900/60 rounded-xl border border-slate-800 flex items-center gap-2.5 font-mono overflow-x-auto">
        <span className={`flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-bold ${METHOD_COLOR[doc.method] || 'bg-slate-700 text-slate-300'}`}>
          {doc.method}
        </span>
        <span className="text-xs text-slate-400 whitespace-nowrap">{liveUrl}</span>
      </div>

      {/* Params */}
      {(params.length > 0) && (
        <div className="px-4 py-3 mt-1 space-y-3 border-t border-slate-800/50 mt-3">
          {pathParams.map(p => (
            <FieldInput key={p.name} param={p} value={fieldValues[p.name] || ''} onChange={v => handleField(p.name, v)} />
          ))}
          {queryParams.map(p => (
            <FieldInput key={p.name} param={p} value={fieldValues[p.name] || ''} onChange={v => handleField(p.name, v)} />
          ))}
          {bodyParams.map(p => (
            p.isFile
              ? <FileFieldInput key={p.name} param={p} fileName={fileNames[p.name]} onChange={(f, n) => handleFile(p.name, f, n)} />
              : <FieldInput key={p.name} param={p} value={fieldValues[p.name] || ''} onChange={v => handleField(p.name, v)} />
          ))}
          {extraHeaderParams.map(p => (
            <FieldInput key={p.name} param={p} value={fieldValues[p.name] || ''} onChange={v => handleField(p.name, v)} />
          ))}
        </div>
      )}

      {/* Send */}
      <div className="px-4 py-3 border-t border-slate-800/50">
        <button
          onClick={handleSend}
          disabled={loading}
          data-testid="api-tester-send-btn"
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg transition-colors shadow-lg shadow-blue-900/20"
        >
          {loading
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
            : <><Play className="w-3.5 h-3.5" /> Send Request</>}
        </button>
      </div>

      {/* Live response */}
      {result && (
        <div className="border-t border-slate-800">
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900">
            <div className="flex items-center gap-2.5">
              <StatusBadge status={result.status} />
              {result.time > 0 && (
                <span className="text-[11px] text-slate-500 font-mono">{result.time}ms</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-600 uppercase tracking-wide">Response</span>
              <button onClick={copyResponse}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300 transition-colors">
                {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
              </button>
            </div>
          </div>

          {/* Image preview for TryOn / Moments results */}
          {result.imageUrl && result.ok && (
            <div className="px-4 pt-4 pb-3 border-b border-slate-800 bg-slate-900/40">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-2.5">
                Result Preview
              </p>
              {/* Clickable image thumbnail → lightbox */}
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="w-full group relative rounded-xl overflow-hidden bg-slate-900 border border-slate-800 cursor-zoom-in block"
                data-testid="result-image-preview-btn"
              >
                <img
                  src={result.imageUrl}
                  alt="Result preview"
                  className="w-full object-contain max-h-64 transition-transform duration-300 group-hover:scale-[1.02]"
                  data-testid="result-image-preview"
                  onError={e => { e.currentTarget.closest('button').style.display = 'none'; }}
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded-full p-2.5">
                    <Maximize2 className="w-4 h-4 text-white" />
                  </div>
                </div>
              </button>
              <p className="mt-2 text-[10px] text-slate-600 text-center">Click image to view full screen</p>
            </div>
          )}

          {lightboxOpen && result.imageUrl && (
            <ImageLightbox src={result.imageUrl} onClose={() => setLightboxOpen(false)} />
          )}

          <pre className="p-4 text-xs leading-relaxed font-mono text-slate-300 whitespace-pre overflow-auto max-h-72">
            {result.body}
          </pre>
        </div>
      )}
    </div>
  );
}
